import { Component, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { TestEvents } from "../../../services/TestEvents";
import { TestAuth } from "../../../services/TestAuth";
import { User } from "../../../services/User";
import {
  UPDATE_ACTIONS,
  LOGIN_STATE_ACTIONS,
  UpdateAction,
  LoginStateAction,
  IUserUpdatePayload,
  IUserMergePayload,
  ICmAssignmentPayload,
  IUserLoginStatePayload,
} from "../../../interfaces/ITestEvent";
import { IUser, ICmUserInfo } from "../../../interfaces/IUser";

type GroupedAction = UpdateAction | "users_merged" | "cm_assignment_set" | LoginStateAction;

const GROUPED_ACTIONS: readonly GroupedAction[] = [
  ...UPDATE_ACTIONS,
  "users_merged",
  "cm_assignment_set",
  ...LOGIN_STATE_ACTIONS,
];

@Component({
  selector: "test-events-update",
  templateUrl: "./TestEventsUpdateCmp.html",
  styleUrls: ["./TestEventsUpdateCmp.css"],
})
export class TestEventsUpdateCmp implements OnInit {
  readonly allActions = GROUPED_ACTIONS;
  action: GroupedAction = "profile_updated";
  entity: string = "Contact";
  entityId: number = 99999;

  updatePayload: IUserUpdatePayload = { user: { id: 99999 } as IUser };
  merge: IUserMergePayload = { source: "rust-crm" };
  cmAssignment: ICmAssignmentPayload = {
    cm_emails: [],
    assignment_time: "",
    source: "rust-crm",
  };
  loginState: IUserLoginStatePayload = { source: "rust-crm" };

  cmUsersList: IUser[] = [];
  selectedUser: IUser | null = null;
  resultMessage = "";
  resultType: "success" | "error" | "" = "";
  loadingUser = false;
  userIdError = "";

  constructor(
    private route: ActivatedRoute,
    private testEvents: TestEvents,
    public testAuth: TestAuth,
    private userService: User
  ) {}

  ngOnInit() {
    this.route.paramMap.subscribe((params) => {
      const a = params.get("action");
      if (a && (GROUPED_ACTIONS as readonly string[]).includes(a)) {
        this.action = a as GroupedAction;
      }
    });
    const now = new Date().toISOString().replace("T", " ").slice(0, 19);
    this.cmAssignment.assignment_time = now;
  }

  isUpdateAction(): boolean {
    return (UPDATE_ACTIONS as readonly string[]).includes(this.action);
  }
  isLoginStateAction(): boolean {
    return (LOGIN_STATE_ACTIONS as readonly string[]).includes(this.action);
  }

  get primaryUserIdModel(): number {
    return this.updatePayload.user?.id ?? this.entityId;
  }
  set primaryUserIdModel(v: number) {
    if (this.updatePayload.user) this.updatePayload.user.id = v;
    this.entityId = v;
  }

  onUserIdBlur() {
    if (this.primaryUserIdModel > 0) this.loadUserById(this.primaryUserIdModel);
  }

  loadUserById(userId: number) {
    this.loadingUser = true;
    this.userIdError = "";
    this.userService.getFreshUserInfo(userId).subscribe({
      next: (user) => {
        this.loadingUser = false;
        this.selectedUser = user;
        this.populate(user);
      },
      error: () => {
        this.loadingUser = false;
        this.userIdError = "User not found";
      },
    });
  }

  onUserSelected(user: IUser) {
    if (!user) {
      this.selectedUser = null;
      return;
    }
    this.selectedUser = user;
    this.loadingUser = true;
    this.userService.getFreshUserInfo(user.id).subscribe({
      next: (fullUser) => {
        this.loadingUser = false;
        this.populate(fullUser);
      },
      error: () => {
        this.loadingUser = false;
        this.populate(user);
      },
    });
  }

  private populate(user: IUser) {
    this.updatePayload.user = {
      ...user,
      __cm_users: user.__cm_users ? [...user.__cm_users] : [],
    };
    this.entityId = user.id;
    this.loginState = {
      email: user.email,
      source: "rust-crm",
      __creator_email: user.__creator_email,
      __updated_by_email: user.__updated_by_email,
    };
    this.cmAssignment.contact_email = user.email;
    this.cmAssignment.__creator_email = user.__creator_email;
    this.cmAssignment.__updated_by_email = user.__updated_by_email;
    this.merge.dest_email = user.email;
    this.merge.__creator_email = user.__creator_email;
    this.merge.__updated_by_email = user.__updated_by_email;
    if (user.cm_access_level > 0) this.entity = "ClientManager";
    else if (user.admin_access_level > 0) this.entity = "Admin";
    else if (user.distr_access_level > 0) this.entity = "Rep";
    else this.entity = "Contact";
  }

  addCmUser(user: IUser) {
    if (!user || !user.email) return;
    if (this.cmAssignment.cm_emails.indexOf(user.email) >= 0) return;
    this.cmAssignment.cm_emails = [...this.cmAssignment.cm_emails, user.email];
    this.cmUsersList = [...this.cmUsersList, user];
  }

  removeCmUser(index: number) {
    this.cmAssignment.cm_emails = this.cmAssignment.cm_emails.filter((_, i) => i !== index);
    this.cmUsersList = this.cmUsersList.filter((_, i) => i !== index);
  }

  addUserCm(user: IUser) {
    if (!user || !user.id) return;
    if (!this.updatePayload.user.__cm_users) this.updatePayload.user.__cm_users = [];
    if (this.updatePayload.user.__cm_users.some((cm) => cm.id === user.id)) return;
    const cm: ICmUserInfo = {
      id: user.id,
      fname: user.fname,
      lname: user.lname,
      email: user.email,
    };
    this.updatePayload.user.__cm_users = [...this.updatePayload.user.__cm_users, cm];
  }

  removeUserCm(index: number) {
    if (!this.updatePayload.user.__cm_users) return;
    this.updatePayload.user.__cm_users = this.updatePayload.user.__cm_users.filter((_, i) => i !== index);
  }

  onSubmit() {
    this.resultMessage = "";
    this.resultType = "";
    const ok = (r: any) => {
      this.resultType = "success";
      this.resultMessage = `Event sent. id=${r.event_id || "N/A"}`;
    };
    const err = (e: any) => {
      this.resultType = "error";
      this.resultMessage = e.error?.error || "Failed to send event";
    };

    if (this.isUpdateAction()) {
      this.testEvents
        .sendUpdate({ entity: this.entity, action: this.action, payload: this.updatePayload })
        .subscribe({ next: ok, error: err });
      return;
    }
    if (this.action === "users_merged") {
      this.testEvents
        .sendUsersMerged({ entity: this.entity, entity_id: this.entityId, payload: this.merge })
        .subscribe({ next: ok, error: err });
      return;
    }
    if (this.action === "cm_assignment_set") {
      this.testEvents
        .sendCmAssignmentSet({ entity_id: this.entityId, payload: this.cmAssignment })
        .subscribe({ next: ok, error: err });
      return;
    }
    if (this.isLoginStateAction()) {
      this.testEvents
        .sendLoginState({
          entity: this.entity,
          entity_id: this.entityId,
          action: this.action,
          payload: this.loginState,
        })
        .subscribe({ next: ok, error: err });
      return;
    }
  }
}
